/**
 * Responsibility: one discovery board as a table, with everything a buy
 * decision needs on the row.
 *
 * Ordered by the hub and never re-sorted here. The ranking encodes a scarcity
 * and price judgement the client cannot reproduce, and a click-to-sort that
 * quietly discarded it would put the least trustworthy rows back on top -
 * sorting by population ascending is exactly how the phantom cards surface.
 *
 * The two profit columns are the point of the table, and they are shown
 * together on purpose. The top modern card returns +$1,130 if one copy gems
 * and -$165 on average, because only one submission in two hundred actually
 * gems it. Either number alone is misleading; side by side they are the
 * decision.
 *
 * Gem rate gets its own column rather than a footnote under the population,
 * because it is what converts a multiple into a probability and it is the
 * single figure that most often kills an attractive-looking row.
 */
import { useState } from 'react';
import { NameSortHeader } from '../shelf/NameSortHeader';
import { headerNode } from '../shelf/headerNode';
import { nameSortValue } from '../shelf/nameSort';
import type { NameSort } from '../shelf/nameSort';
import { sortableNumber } from '../shelf/sortValues';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import BookmarkIcon from '@mui/icons-material/BookmarkAddOutlined';
import WarningIcon from '@mui/icons-material/WarningAmber';
import VerifiedIcon from '@mui/icons-material/CheckCircleOutline';
import Chip from '@mui/material/Chip';
import { DataTable } from '@kit/components/data';
import type { DataColumn } from '@kit/components/data';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { ToolbarIconButton } from '@kit/components/buttons';
import { EMPTY } from '@kit/utils';
import type { DiscoveredCard } from '../../api/client';
import { CardCell, CostCell, GradeCell, ProfitCell } from './DiscoveryCells';
import { money, multiple, percent, toNumber } from './money';

type DiscoveryTableProps = {
  rows: DiscoveredCard[];
  loading?: boolean;
  busy?: boolean;
  onArchive: (row: DiscoveredCard) => void;
  /** Takes the card off the board too, but as one worth buying. */
  onWatch: (row: DiscoveredCard) => void;
  /**
   * Keep the card without taking it off the board.
   *
   * Different from watching: watching says "I am done considering this,
   * hold it aside", liking says "remember this one" and leaves it in the
   * ranking where it can still be compared against its neighbours.
   */
  onLike: (row: DiscoveredCard) => void;
  /** Keys already liked, so the heart renders filled. */
  liked: Set<string>;
  onOpen: (row: DiscoveredCard) => void;
  /** Desktop cap, measured against the viewport by the page. */
  maxHeight?: number;
};

/** See WatchTable: effectively no cap on phones, without patching the kit. */
const UNCAPPED = 100_000;

/** Below this share of submissions, a gem is rare enough to doubt the bet. */
const POOR_GEM_RATE = 0.05;

export function DiscoveryTable({
  rows,
  loading = false,
  busy = false,
  onArchive,
  onWatch,
  onLike,
  liked,
  onOpen,
  maxHeight = 640,
}: DiscoveryTableProps) {
  // Which of the two name orderings the card column uses.
  const [nameSort, setNameSort] = useState<NameSort>('alphabetical');
  const columns: DataColumn<DiscoveredCard>[] = [
    { id: 'card', sortValue: (row: DiscoveredCard) => nameSortValue(`${row.card_name} #${row.card_number}`, nameSort), label: headerNode(<NameSortHeader label="Card" value={nameSort} onChange={setNameSort} />), render: (row) => <CardCell row={row} /> },
    { id: 'cost', sortValue: (row: DiscoveredCard) => sortableNumber(row.raw_cost), label: 'Raw', width: 100, render: (row) => <CostCell row={row} /> },
    {
      id: 'psa10',
      sortValue: (row: DiscoveredCard) => sortableNumber(row.reference_psa10),
      label: 'PSA 10',
      width: 105,
      align: 'right',
      render: (row) => <GradeCell price={row.reference_psa10} population={row.pop_psa10} />,
    },
    {
      id: 'psa9',
      sortValue: (row: DiscoveredCard) => sortableNumber(row.reference_psa9),
      label: 'PSA 9',
      width: 105,
      align: 'right',
      render: (row) => <GradeCell price={row.reference_psa9} population={row.pop_psa9} />,
    },
    {
      id: 'gem',
      sortValue: (row: DiscoveredCard) => sortableNumber(row.gem_rate),
      label: 'Gem %',
      // The heading needs 104 with its arrow; 95 wrapped it.
      width: 106,
      align: 'right',
      render: (row) => {
        const rate = toNumber(row.gem_rate);
        return (
          <Stack spacing={0} sx={{ textAlign: 'right' }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
              color={
                rate === null
                  ? 'text.disabled'
                  : rate < POOR_GEM_RATE
                    ? 'warning.main'
                    : 'text.primary'
              }
            >
              {rate === null ? EMPTY : percent(row.gem_rate, 1)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.pop_total.toLocaleString()} total
            </Typography>
          </Stack>
        );
      },
    },
    {
      id: 'multiple',
      sortValue: (row: DiscoveredCard) => sortableNumber(row.multiple),
      label: 'Mult',
      // Matches search, where the heading is clipped below this.
      width: 92,
      align: 'right',
      render: (row) => (row.multiple === null ? EMPTY : multiple(row.multiple)),
    },
    {
      id: 'best',
      sortValue: (row: DiscoveredCard) => sortableNumber(row.best_case_profit),
      label: 'If gems',
      width: 105,
      align: 'right',
      render: (row) => (
        <ProfitCell
          value={row.best_case_profit}
          hint={`Buy the batch, assume exactly one comes back a 10, sell the rest as 9s. Costs ${money(row.total_cost)} all in.`}
        />
      ),
    },
    {
      id: 'expected',
      sortValue: (row: DiscoveredCard) => sortableNumber(row.expected_profit),
      label: 'Avg',
      width: 95,
      align: 'right',
      render: (row) => (
        <ProfitCell
          value={row.expected_profit}
          hint="The average outcome at this card's real gem rate. Where this is negative and the best case is not, the card is a lottery ticket."
        />
      ),
    },
    {
      id: 'trust',
      label: 'Trust',
      // An icon's width plus its heading. It held "unverified" before,
      // which was the only thing it ever said and not worth ninety-six
      // pixels - but 60 clipped the word "Trust" itself, so this is what
      // the heading measures at rather than what the icon needs.
      width: 78,
      align: 'center',
      render: (row) => {
        if (row.reference_disagrees) {
          return (
            <WarningIcon fontSize="small" color="error" />
          );
        }
        if (!row.still_affordable) {
          return (
            <WarningIcon fontSize="small" color="warning" />
          );
        }
        if (row.verified) {
          return (
            <VerifiedIcon fontSize="small" color="success" />
          );
        }
        // **Nothing, rather than "unverified" on every row.** Verifying
        // a cost needs TCGplayer data that is not flowing, so the word
        // appeared on every card and told nobody anything - a column of
        // one repeated word is a column of noise. The warnings and the
        // tick above still say something, so the column stays and is
        // simply quiet when it has nothing to report.
        return null;
      },
    },
    {
      id: 'actions',
      label: '',
      width: 128,
      align: 'right',
      render: (row) => (
        <Stack direction="row" spacing={0.25} justifyContent="flex-end">
          <ToolbarIconButton
            aria-label={
              liked.has(row.discovery_key)
                ? 'Liked - see it on the Likes page'
                : 'Like, to keep it without taking it off the board'
            }
            onClick={(event) => {
              event.stopPropagation();
              onLike(row);
            }}
            disabled={busy || liked.has(row.discovery_key)}
          >
            {liked.has(row.discovery_key) ? (
              <FavoriteIcon fontSize="small" color="error" />
            ) : (
              <FavoriteBorderIcon fontSize="small" />
            )}
          </ToolbarIconButton>
          <ToolbarIconButton
            aria-label="Move to the watch list"
            onClick={(event) => {
              // The row itself opens the detail, so both actions have to
              // stop the click before it gets there.
              event.stopPropagation();
              onWatch(row);
            }}
            disabled={busy}
          >
            <BookmarkIcon fontSize="small" />
          </ToolbarIconButton>
          <ToolbarIconButton
            aria-label="Move to the bin"
            onClick={(event) => {
              event.stopPropagation();
              onArchive(row);
            }}
            disabled={busy}
          >
            <DeleteIcon fontSize="small" />
          </ToolbarIconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={1} sx={{ minHeight: 0, flexGrow: 1 }}>
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.discovery_key}
        loading={loading}
        emptyMessage="No cards pass the screen at this floor. Try a lower population floor, or refresh the data."
        maxHeight={{ xs: UNCAPPED, md: maxHeight }}
        onRowClick={onOpen}
      />
    </Stack>
  );
}

/** Shown above the table: what the board is drawn from, and under what costs. */
export function DiscoverySummary({
  total,
  shown,
  universeSize,
  built,
  cardsBought,
  gradingCost,
  shipping,
  feeRate,
}: {
  total: number;
  shown: number;
  universeSize: number;
  built: string;
  cardsBought: number;
  gradingCost: string;
  shipping: string;
  feeRate: string;
}) {
  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip size="small" variant="outlined" label={`showing ${shown} of ${total}`} />
        <Typography variant="caption" color="text.secondary">
          screened from {universeSize.toLocaleString()} cards
          {built ? ` · population data built ${built}` : ''}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Payoffs assume buying {cardsBought}, {money(gradingCost)} per card to grade,{' '}
        {money(shipping)} shipping, {percent(feeRate, 0)} marketplace fee. Graded prices are
        references, not verified sales.
      </Typography>
    </Stack>
  );
}
